import type { PdpEcommerceBlockProductPdpMessageSideSlideProps } from '@design-system/components/Pdp/EcommerceBlock/PdpEcommerceBlockProductPdpMessageSideSlide.props'

/**
 * Composable for fetching and managing product pdp message.
 *
 * @param {string} deliveryKey - The key used to fetch specific product pdp message.
 * @returns {Object} An object containing the product pdp message.
 * @property {Ref<ProductFeatures | null>} productPdpMessage - Reactive state of product pdp message.
 *
 * @example
 * const { productPdpMessage } = useProductPdpMessage('product-care-items')
 *
 * @remarks
 * This composable handles fetching product pdp message with fallback mechanisms.
 * It supports localized content retrieval and handles potential fetch errors.
 */
export const useProductPdpMessage = (deliveryKey: string) => {
  const localePath = useLocalePath()
  const route = useRoute()

  /**
   * Reactive state to store product pdp message.
   *
   * @type {Ref<PdpMessage | null>}
   */
  const productPdpMessage: Ref<PdpEcommerceBlockProductPdpMessageSideSlideProps | null> =
    useState('productPdpMessage', () => null)

  /**
   * Computed delivery key to trigger reactivity.
   *
   * @type {ComputedRef<string>}
   */
  const deliveryKeyComp = computed(() => deliveryKey)

  /**
   * Fetches product pdp message from the CMS API.
   *
   * @param {string} key - The delivery key for fetching features.
   * @returns {Promise<any>} A promise resolving to the fetched product pdp message.
   * @throws {Error} If there's an issue fetching the product pdp message.
   *
   * @private
   */
  const fetchProductPdpMessage = async (key: string) => {
    const query = {
      locale: localePath(''),
      vse: route.query.vse,
      contentId: route.query.contentId,
      deliveryKey: key,
    }

    try {
      return await $fetch('/api/cms/getProductPdpMessage', { query })
    } catch (error) {
      console.error(`Error fetching product pdp message for key: ${key}`, error)
      throw error // rethrow the error for the caller to handle
    }
  }

  /**
   * Loads product pdp message with a fallback mechanism.
   *
   * @async
   * @throws {Error} If both primary and fallback fetches fail.
   *
   * @remarks
   * This method attempts to fetch pdp message with a country-specific key first,
   * then falls back to the original delivery key if the first attempt fails.
   */
  const loadProductPdpMessage = async () => {
    // Commented out country-specific fallback for now
    // try {
    //   const response = await fetchProductPdpMessage(`${deliveryKey}-${country}`)
    //   productPdpMessage.value = response.content
    // } catch {
    try {
      const fallbackResponse = await fetchProductPdpMessage(deliveryKey)
      productPdpMessage.value = {
        title: fallbackResponse.content.title ?? '',
        message: fallbackResponse.content.message ?? '',
        media: fallbackResponse.content.media ?? {
          type: 'image',
          url: {
            secure_url: '',
          },
          alt: '',
        },
        label: `${deliveryKey}.label`,
        ctaLabel: fallbackResponse.content.ctaText ?? '',
        cta: fallbackResponse.content.cta ?? null,
      }
    } catch (error) {
      productPdpMessage.value = null
      console.error('Failed to load product pdp message:', error)
    }
    // }
  }

  /**
   * Watches the delivery key and triggers feature loading.
   *
   * @remarks
   * Automatically loads product pdp message when the delivery key changes,
   * with an immediate initial load.
   */
  watch(
    deliveryKeyComp,
    () => {
      loadProductPdpMessage()
    },
    { immediate: true }
  )

  return {
    productPdpMessage,
  }
}
